import {
  AHText,
  AHWidgetTitle,
  Box,
  Circle,
  ColorType,
  useScaleFactor,
} from "arkh";
import React, { PropsWithChildren, ReactNode } from "react";

import { DateTime } from "luxon";
import { EmptyResult } from "../components/EmptyResult";
import { Platform } from "react-native";
import { TaskDate } from "./TaskDate";
import { colorForNumber } from "./NumberTask/colorForNumber";
import { formatNumberValue } from "./NumberTask/formatNumber";
import { Section } from "./useWidgetTaskSingleSections";
import { graphql, useFragment } from "react-relay";
import { SingleTaskInProgressCheck_taskExecutionCheck$key } from "./__generated__/SingleTaskInProgressCheck_taskExecutionCheck.graphql";
import { TaskExecutionCheckSentimentIcon } from "../components/TaskExecutionCheckSentimentIcon";
import { NumberTaskUnit } from "./NumberTask/__generated__/WidgetTaskSingleNumberOccasionPage_taskNumber.graphql";
import { useCheckTaskExecutionList } from "./taskExecutionList";
import {
  TaskExecutionGroupItem,
  TaskExecutionTotalGroup,
} from "./useGroupNumberTaskExecution";

const DetailWidth = "39%";
export function TaskExecutionLatestList(props: { children: ReactNode }) {
  return (
    <Box style={{ width: DetailWidth }} spaceRight="small">
      <AHWidgetTitle spaceLeft="none" spaceBottom="none">
        Resultat
      </AHWidgetTitle>
      <Box spaceTop={Platform.OS === "android" ? "none" : "small"}>
        {props.children}
      </Box>
    </Box>
  );
}
function TaskExecutionLatestListItemValueText(
  props: PropsWithChildren<{ color: ColorType }>
) {
  return (
    <AHText
      spaceRight="xxsmall"
      numberOfLines={1}
      style={{
        height: "100%",
      }}
      color={props.color}
      type="subtitle"
    >
      {props.children}
    </AHText>
  );
}
function TaskExecutionLatestListItemC(
  props: PropsWithChildren<{
    label: string;
    bold?: boolean;
    color?: string;
  }>
) {
  const sf = useScaleFactor();
  return (
    <Box
      style={{
        flexDirection: "row",
        height: Math.ceil(sf * 18),
        justifyContent: "space-between",
        alignItems: "flex-start",
      }}
    >
      <AHText
        spaceRight="xxsmall"
        numberOfLines={1}
        style={{ flex: 1, height: "100%" }}
        type={props.bold ? "subtitle" : "body"}
      >
        <Box spaceRight="xsmall">
          <Circle size="dot" style={{ backgroundColor: props.color }} />
        </Box>
        {props.label}
      </AHText>
      {props.children}
    </Box>
  );
}
export const TaskExecutionLatestListItem = React.memo(
  TaskExecutionLatestListItemC
);

export function SingleTaskInProgress(props: {
  items: TaskExecutionGroupItem[];
  lastUpdated: string | null;
  unit: NumberTaskUnit;
  total: TaskExecutionTotalGroup;
}) {
  const visibleItems = props.items.filter((items) => items.hidden !== true);
  if (visibleItems.length === 1) {
    const lastUpdatedInProgress = props.lastUpdated
      ? DateTime.fromISO(props.lastUpdated)
      : null;
    const item = visibleItems[0];
    return (
      <Box
        style={{
          width: DetailWidth,
          justifyContent: "center",
          alignContent: "center",
        }}
      >
        <AHText
          style={{ textAlign: "center" }}
          type="stat-number"
          numberOfLines={1}
          adjustsFontSizeToFit
          minimumFontScale={0.6}
          color={colorForNumber(
            item.numberValue,
            item.goals?.lessThan ?? null,
            item.goals?.greaterThan ?? null
          )}
        >
          {item.numberValue === undefined || item.numberValue === null ? (
            <EmptyResult />
          ) : (
            formatNumberValue(props.unit, item.numberValue)
          )}
        </AHText>
        <TaskDate date={lastUpdatedInProgress} />
      </Box>
    );
  }
  return (
    <TaskExecutionLatestList>
      {visibleItems.map((groupItem) => {
        return (
          <TaskExecutionLatestListItem
            key={groupItem.groupId}
            label={groupItem.label}
            color={groupItem.color ?? undefined}
          >
            <TaskExecutionLatestListItemValueText
              color={colorForNumber(
                groupItem.numberValue,
                groupItem.goals?.lessThan ?? null,
                groupItem.goals?.greaterThan ?? null
              )}
            >
              {formatNumberValue(
                props.unit,
                groupItem.numberValue ?? undefined
              ) ?? ""}
            </TaskExecutionLatestListItemValueText>
          </TaskExecutionLatestListItem>
        );
      })}
      <TaskExecutionLatestListItem bold label={props.total.label} color="#000">
        <TaskExecutionLatestListItemValueText
          color={colorForNumber(
            props.total.numberValue,
            props.total.goals?.lessThan ?? null,
            props.total.goals?.greaterThan ?? null
          )}
        >
          {formatNumberValue(
            props.unit,
            props.total.numberValue ?? undefined
          ) ?? ""}
        </TaskExecutionLatestListItemValueText>
      </TaskExecutionLatestListItem>
    </TaskExecutionLatestList>
  );
}

export function SingleTaskInProgressCheck(props: {
  taskExecutions: SingleTaskInProgressCheck_taskExecutionCheck$key;
  sections: Section[];
  maxParts?: number;
}) {
  const taskExecutions = useFragment(
    graphql`
      fragment SingleTaskInProgressCheck_taskExecutionCheck on TaskExecutionCheck
      @relay(plural: true) {
        ...taskExecutionListCheckTaskExecutions
        checkValue
        updatedAt
      }
    `,
    props.taskExecutions
  );
  const taskExecutionDetailList = useCheckTaskExecutionList(
    taskExecutions,
    props.sections,
    props.maxParts
  );

  if (props.taskExecutions.length === 1) {
    const updatedLastAt = taskExecutions[0].updatedAt
      ? DateTime.fromISO(taskExecutions[0].updatedAt)
      : undefined;
    return (
      <Box
        style={{
          width: DetailWidth,
          justifyContent: "center",
          alignContent: "center",
          alignItems: "center",
        }}
      >
        <Box spaceBottom="small">
          <TaskExecutionCheckSentimentIcon
            size="large"
            sentiment={taskExecutions[0].checkValue}
          />
        </Box>
        <TaskDate date={updatedLastAt} />
      </Box>
    );
  }
  return (
    <TaskExecutionLatestList>
      {taskExecutionDetailList.map((te) => (
        <Box spaceBottom="small" key={te.id}>
          <TaskExecutionLatestListItem
            key={te.id}
            label={te.sectionTitle ?? ""}
            bold={te.id === "total"}
            color={te.color ?? undefined}
          >
            <TaskExecutionCheckSentimentIcon
              size="small"
              sentiment={te.checkValue}
            />
          </TaskExecutionLatestListItem>
        </Box>
      ))}
    </TaskExecutionLatestList>
  );
}
